/**
 * User Management Dialogs
 * Contains ChangeRoleDialog, ActivityLogDialog, and BulkApproveDialog
 */

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { CheckCircle, Shield, History, Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Input } from '../../components/ui/input';
import { UserProfile, Role, UserManagementLog, Company, coreAPI } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

function Label({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={className}>{children}</div>;
}

// --- Change Role Dialog ---
interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  availableRoles: Role[];
  selectedRoleId: number | null;
  setSelectedRoleId: (id: number | null) => void;
  actionLoading: boolean;
  onChangeRole: () => void;
}

export function ChangeRoleDialog({
  open,
  onOpenChange,
  selectedUser,
  availableRoles,
  selectedRoleId,
  setSelectedRoleId,
  actionLoading,
  onChangeRole,
}: ChangeRoleDialogProps) {
  // CR-5294: backend rejects assigning a role whose company_type doesn't
  // match the target user's company. Hide incompatible options so the
  // admin can't pick e.g. "Helix Admin" for a customer user and get a
  // silent 400.
  const compatibleRoles = selectedUser
    ? availableRoles.filter((role) => role.company_type === selectedUser.company_type)
    : availableRoles;
  const selectedRole = compatibleRoles.find((r) => r.id === selectedRoleId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change User Role</DialogTitle>
          <DialogDescription>
            Select a new role for {selectedUser?.user.full_name}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label className="text-sm font-medium mb-2 block">Current User Role</Label>
          <Badge variant="outline" className="mb-4">{selectedUser?.role_name || 'No role assigned'}</Badge>

          <Label className="text-sm font-medium mb-2 block">New User Role</Label>
          <Select
            value={selectedRoleId?.toString() || ''}
            onValueChange={(value) => setSelectedRoleId(parseInt(value))}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {compatibleRoles.map((role) => (
                <SelectItem key={role.id} value={role.id.toString()}>
                  <div className="flex items-center gap-2">
                    <span>{role.name}</span>
                    {role.is_admin_role && (
                      <Badge variant="secondary" className="text-xs">Admin</Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedRole && (
            <div className="mt-4 p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">
                {selectedRole.description}
              </p>
              {/* CR-7076: permissions_summary hidden — it was composed from
                  capability flags nothing enforces (dormant). The plain
                  description is the honest blurb. */}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setSelectedRoleId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onChangeRole}
            disabled={actionLoading || !selectedRoleId || selectedRoleId === selectedUser?.role}
          >
            <Shield className="h-4 w-4 mr-2" />
            Change Role
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Activity Log Dialog ---
interface ActivityLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  activityLogs: UserManagementLog[];
}

export function ActivityLogDialog({
  open,
  onOpenChange,
  selectedUser,
  activityLogs,
}: ActivityLogDialogProps) {
  const { formatDateTime } = useFormatters();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>User Activity Log</DialogTitle>
          <DialogDescription>
            Management actions performed on {selectedUser?.user.full_name}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto py-4">
          {activityLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No activity logs found for this user.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activityLogs.map((log) => (
                <div key={log.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant={
                      log.action.includes('enabled') ? 'default' :
                      log.action.includes('disabled') ? 'secondary' :
                      'outline'
                    }>
                      {log.action_display}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(log.timestamp)}
                    </span>
                  </div>
                  <p className="text-sm mb-2">{log.description}</p>
                  <div className="text-xs text-muted-foreground">
                    By: {log.performed_by_name} ({log.performed_by_email})
                  </div>
                  {(Object.keys(log.old_value).length > 0 || Object.keys(log.new_value).length > 0) && (
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      {Object.keys(log.old_value).length > 0 && (
                        <div className="bg-red-50 p-2 rounded">
                          <span className="font-medium">Before:</span>
                          <pre className="mt-1 text-red-700">
                            {JSON.stringify(log.old_value, null, 2)}
                          </pre>
                        </div>
                      )}
                      {Object.keys(log.new_value).length > 0 && (
                        <div className="bg-green-50 p-2 rounded">
                          <span className="font-medium">After:</span>
                          <pre className="mt-1 text-green-700">
                            {JSON.stringify(log.new_value, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Bulk Approve Dialog ---
interface BulkApproveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  pendingInSelectionCount: number;
  actionLoading: boolean;
  onBulkApprove: () => void;
}

export function BulkApproveDialog({
  open,
  onOpenChange,
  selectedCount,
  pendingInSelectionCount,
  actionLoading,
  onBulkApprove,
}: BulkApproveDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve selected users</DialogTitle>
          <DialogDescription>
            {selectedCount} user(s) selected. Only users pending approval will be approved
            ({pendingInSelectionCount} in this selection). Already-approved users are skipped.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onBulkApprove}
            disabled={actionLoading || pendingInSelectionCount === 0}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Approve pending ({pendingInSelectionCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Bulk send invitation emails ---
interface BulkSendInvitationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  actionLoading: boolean;
  onConfirm: () => void;
}

export function BulkSendInvitationsDialog({
  open,
  onOpenChange,
  selectedCount,
  actionLoading,
  onConfirm,
}: BulkSendInvitationsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send invitation emails</DialogTitle>
          <DialogDescription>
            Send a Helix invitation email to each of the {selectedCount} selected user(s).
            You can resend to users who were already invited.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={actionLoading || selectedCount === 0}>
            <Mail className="h-4 w-4 mr-2" />
            Send {selectedCount} email(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Change Company Dialog (platform staff repair tool) ---
interface ChangeCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  actionLoading: boolean;
  /** Relink the user to the chosen company. */
  onRelink: (companyId: number) => void;
}

/**
 * Move a user to a different company — the fix for accounts set up
 * against the wrong tenant. Staff/superuser only (the parent gates the
 * menu item; the backend enforces it again). One searchable picker:
 * type to filter, click a company to choose it; the header shows the
 * move as FROM → TO so what is about to happen is unambiguous.
 */
export function ChangeCompanyDialog({
  open,
  onOpenChange,
  selectedUser,
  actionLoading,
  onRelink,
}: ChangeCompanyDialogProps) {
  const [search, setSearch] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setCompanies([]);
      setSelectedCompany(null);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await coreAPI.getCompanies({ search, page_size: 8 });
        const data = res.data as unknown as { results?: Company[] };
        setCompanies((data.results ?? (res.data as unknown as Company[])) || []);
      } catch (err: unknown) {
        console.error('Failed to load companies:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, open]);

  const typeChanges = Boolean(
    selectedCompany && selectedUser
    && selectedCompany.company_type !== selectedUser.company_type,
  );

  const typeBadge = (companyType?: string) => (
    <Badge variant="secondary" className="text-xs capitalize">{companyType || '—'}</Badge>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Company</DialogTitle>
          <DialogDescription>
            Move {selectedUser?.user.full_name || selectedUser?.user.email} to the
            company they should have been set up under.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          {/* The move, stated as from → to */}
          <div className="flex items-center gap-3 rounded-md border p-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground mb-1">From</div>
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">{selectedUser?.company_name || '—'}</span>
                {typeBadge(selectedUser?.company_type)}
              </div>
            </div>
            <span className="text-muted-foreground shrink-0">→</span>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground mb-1">To</div>
              {selectedCompany ? (
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{selectedCompany.name}</span>
                  {typeBadge(selectedCompany.company_type)}
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Pick a company below</span>
              )}
            </div>
          </div>

          {/* One searchable picker: type, then click the company */}
          <div>
            <Input
              placeholder="Type to search companies…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="mt-2 max-h-52 overflow-y-auto rounded-md border divide-y">
              {searching && companies.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">Searching…</div>
              ) : companies.filter((c) => c.id !== selectedUser?.company).length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">No companies match &quot;{search}&quot;</div>
              ) : (
                companies
                  .filter((c) => c.id !== selectedUser?.company)
                  .map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedCompany(c)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted ${
                        selectedCompany?.id === c.id ? 'bg-primary/10' : ''
                      }`}
                    >
                      <span className="truncate">{c.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {typeBadge(c.company_type)}
                        {selectedCompany?.id === c.id && (
                          <CheckCircle className="h-4 w-4 text-primary" />
                        )}
                      </span>
                    </button>
                  ))
              )}
            </div>
          </div>

          <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground space-y-1">
            <p>
              Their site access moves to the new company (purchasing enabled —
              adjust other modules afterwards in the Sites dialog).
            </p>
            {typeChanges && (
              <p className="text-amber-600 font-medium">
                Company type changes ({selectedUser?.company_type} → {selectedCompany?.company_type}),
                so their current role is cleared — assign a new one after relinking.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={() => selectedCompany && onRelink(selectedCompany.id)}
            disabled={actionLoading || !selectedCompany}
          >
            {actionLoading
              ? 'Relinking…'
              : selectedCompany
                ? `Relink to ${selectedCompany.name}`
                : 'Relink User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
